/**
 * BotModel - corpo humanoide dos inimigos, construido em codigo.
 *
 * Decisoes que importam para desempenho e para a nota visual:
 *
 * 1. GEOMETRIA COMPARTILHADA POR UNIFORME. Cada uniforme (sao 3) gera UMA vez
 *    o corpo inteiro ja mesclado (mergeGeometries) com COR POR VERTICE. Um
 *    capacete com visor, trilhos e fones vira parte de 1 draw call, nao 6. Bot
 *    novo so cria SkinnedMesh apontando para a mesma geometria.
 * 2. TRES draw calls por bot, nao 14. O corpo e UM SkinnedMesh por familia de
 *    textura (pano / equipamento duro) mais o fuzil. Antes eram 14 malhas
 *    rigidas penduradas nas juntas, e cada uma era desenhada TRES vezes por
 *    quadro (passada de cor, shadow map e NormalPass do SSAO): ~42 chamadas por
 *    bot, medidas em 57 na cena de combate - o maior gargalo de render do jogo.
 *    As juntas sao as MESMAS de antes, agora THREE.Bone em vez de Group, e a
 *    animacao procedural nao mudou uma linha: quem passou a mover o vertice e o
 *    esqueleto, e nao a matriz de cada malha.
 * 3. Proporcao humana real: 1.80 m de altura, olho em 1.62, ombro em 1.41,
 *    quadril em 0.95. As caixas de acerto sao caixas INVISIVEIS penduradas nos
 *    mesmos ossos, com o bounding box IDENTICO ao da peca que substituem (e o
 *    unico dado que o TargetRegistry le). Invisivel nao entra em render nenhum:
 *    custa zero draw call e continua acompanhando a animacao osso a osso.
 * 4. Animacao 100% procedural: nao ha arquivo de animacao no projeto. Frequencia
 *    do passo cresce com a velocidade, torso gira independente das pernas, e a
 *    arma aponta para o alvo de verdade (a mira do bot e visivel de longe).
 *
 * Silhueta: capacete com calota + mochila + colete criam uma silhueta que se le
 * a 60 m contra o ceu do fim de tarde, que e quando o jogador precisa decidir se
 * atira. Uma faixa de identificacao discreta (cor `mark`) no capacete e no braco
 * evita que o jogador confunda inimigo com cenario.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const TAU = Math.PI * 2;
const HALF_SQ2 = 0.70710678;

/** Alturas das juntas, em metros a partir do pe. Base de toda a proporcao. */
const HIP_Y = 0.95;
const SPINE_Y = 0.10;     // relativo ao quadril  -> 1.05
const NECK_Y = 0.45;      // relativo a espinha   -> 1.50
const SHOULDER_X = 0.195;
const SHOULDER_Y = 0.355; // relativo a espinha   -> 1.405
const ELBOW_Y = -0.285;
const HIPJOINT_X = 0.105;
const HIPJOINT_Y = -0.055; // relativo ao quadril -> 0.895
const KNEE_Y = -0.445;     // relativo a junta do quadril -> 0.45
const ANKLE_Y = -0.375;    // relativo ao joelho -> 0.075 (altura do tornozelo)
const LEG_UPPER = 0.445;
const LEG_LOWER = 0.375;
const ANKLE_HEIGHT = 0.075;  // altura do tornozelo com a sola no chao

/**
 * ORDEM DOS OSSOS. O indice aqui E o skinIndex gravado na geometria, entao esta
 * tabela e a unica fonte da verdade da ligacao "peca -> osso". Mudar a ordem sem
 * regerar a geometria embaralha o corpo.
 */
const BONE = {
  hips: 0, spine: 1, neck: 2,
  shoulderL: 3, elbowL: 4, shoulderR: 5, elbowR: 6,
  hipL: 7, kneeL: 8, ankleL: 9,
  hipR: 10, kneeR: 11, ankleR: 12
};
const BONE_COUNT = 13;

/**
 * Volume de culling do corpo, no espaco da RAIZ do bot.
 *
 * Precisa ser escrito a mao: cada vertice da geometria mesclada mora no espaco do
 * SEU osso, entao o bounding box calculado da geometria nao descreve corpo nenhum
 * (e o three cullaria o bot em posicao errada, ou o faria piscar). Uma esfera na
 * cintura com 1.5 m de raio cobre de sobra o corpo de pe (1.85 m de topo), o
 * fuzil esticado e o corpo caido - que gira junto com a raiz e por isso continua
 * dentro da mesma esfera.
 */
const BODY_CENTER_Y = 0.95;
const BODY_RADIUS = 1.5;

/**
 * O coturno perdeu o material proprio (ver getTemplates): pano, equipamento duro
 * e fuzil sao 3 materiais, e coturno agora entra no equipamento duro. Para o
 * albedo final do coturno nao mudar, a cor de vertice do pe absorve a razao entre
 * os dois multiplicadores que existiam (0.90 do coturno / 0.97 do equipamento).
 */
const BOOT_ALBEDO_FIX = 0.90 / 0.97;

/**
 * POSTURA PARADO. Antes nao existia: parado, `stride` valia 0, o alvo do
 * tornozelo era exatamente HIPJOINT_X (0.105) e o tornozelo terminava em
 * `rotation.set(pitch, 0, 0)`. Somando as tres coisas, o soldado parava com os
 * pes a 21 cm de centro a centro (o pe tem 10 cm de largura: praticamente
 * colados), no MESMO Z e com as duas pontas exatamente para a frente. Isso e
 * posicao de sentido - postura de forma militar, nao de quem esta com o fuzil
 * em punho numa rua.
 *
 * Baixa-pronta de verdade: pes afastados, pe de apoio adiantado, pontas abertas
 * e joelho de tras mais flexionado que o da frente. O lado de apoio e o
 * ESQUERDO, que e onde a IK ja prende a mao do guarda-mao (ver a IK de bracos):
 * pe adiantado do mesmo lado da mao adiantada.
 *
 * O QUADRIL PRECISA DESCER JUNTO, e essa parte nao e enfeite. De pe a perna fica
 * em extensao TOTAL: quadril em 0.895, tornozelo em 0.075, ou seja 0.82 - que e
 * exatamente LEG_UPPER + LEG_LOWER. Qualquer afastamento lateral ou para a frente
 * aumenta a distancia pedida, a IK bate no limitador de alcance (maxR) e o pe
 * sobe do chao em vez de abrir. Descendo 2.6 cm o quadril, os dois pes voltam a
 * caber dentro do alcance e ainda ganham a flexao de joelho da postura
 * (frente ~17 graus, tras ~24 graus).
 */
const STANCE_WIDTH = 0.100;   // afastamento lateral extra por pe -> 41 cm entre pes
const STANCE_LEAD = 0.130;    // pe de apoio (esquerdo) adiantado
const STANCE_TRAIL = -0.050;  // pe de tras recuado
const STANCE_TOE = 0.20;      // abertura da ponta do pe (rad), sinal por lado
const STANCE_DROP = 0.026;    // o quadril desce: sem isso a IK trava no alcance
/** Transferencia de peso de um pe para o outro. Lenta e curta - nao e gingado. */
const IDLE_SWAY = 0.012;
const IDLE_SWAY_HZ = 0.62;

/**
 * Tres uniformes. Nenhum tom abaixo de ~0x26: preto puro sob sol forte denuncia amador.
 *
 * O 'deserto' saiu bem mais escuro e menos saturado do que era (cloth 0x9c8c64):
 * aquele tom, multiplicado por um brilho que chegava a 1.04 e revelado por um sol
 * de fim de tarde com ACES, estourava em mostarda e fazia o soldado parecer um
 * boneco de plastico. Junto com isso, o VALOR entre pano, colete e coturno foi
 * aberto de proposito: e a diferenca de tom entre as partes - nao o relevo - que
 * faz a silhueta ser lida como soldado a 25 m.
 */
const UNIFORMS = [
  {
    name: 'urbano',
    cloth: 0x4f5449, clothDark: 0x3e4238, vest: 0x31352f, gear: 0x2b2e2c,
    helmet: 0x363a32, pad: 0x2a2c2f, boot: 0x27282a, glove: 0x2a2c2e,
    skin: 0x323337, lens: 0x283037, metal: 0x3a3d42, mark: 0x8e4326,
    weapon: 0x2f3237, weaponDark: 0x272a2f
  },
  {
    name: 'deserto',
    cloth: 0x8a7c58, clothDark: 0x6d6144, vest: 0x554d38, gear: 0x433d2e,
    helmet: 0x655c43, pad: 0x35312a, boot: 0x2f2a22, glove: 0x362e25,
    skin: 0x42392e, lens: 0x2e3128, metal: 0x413f37, mark: 0x994b28,
    weapon: 0x37352c, weaponDark: 0x2d2c25
  },
  {
    name: 'noturno',
    cloth: 0x393d43, clothDark: 0x2e3237, vest: 0x272a30, gear: 0x26282c,
    helmet: 0x2c2f36, pad: 0x27292d, boot: 0x26272a, glove: 0x282a2d,
    skin: 0x2f3136, lens: 0x27313b, metal: 0x3c3f45, mark: 0x82392a,
    weapon: 0x2b2e34, weaponDark: 0x25282d
  }
];

// --- temporarios de modulo (zero alocacao no laco de frame) -----------------
const _m4 = new THREE.Matrix4();
const _eu = new THREE.Euler();
const _col = new THREE.Color();
const _q1 = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _axis = new THREE.Vector3();
const _upAxis = new THREE.Vector3(0, 1, 0);

// temporarios da IK de braco
const _ikD = new THREE.Vector3();
const _ikU = new THREE.Vector3();
const _ikPole = new THREE.Vector3();
const _ikAxis = new THREE.Vector3();
const _ikX = new THREE.Vector3();
const _ikY = new THREE.Vector3();
const _ikZ = new THREE.Vector3();
const _ikT = new THREE.Vector3();
const _ikM = new THREE.Matrix4();

/** Comprimento dos ossos do braco (ombro->cotovelo, cotovelo->punho). */
const ARM_UPPER = 0.285;
const ARM_FORE = 0.270;
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();

/**
 * IK de duas ossadas para o braco.
 *
 * Por que IK e nao angulos escritos a mao: as duas maos precisam ficar NA ARMA
 * em toda pose - carregada, ombreada, apontando 30 graus para cima, no meio da
 * recarga, com coice. Angulo fixo acerta uma pose e erra todas as outras (o
 * sintoma classico e a mao esquerda flutuando ao lado do fuzil). Com IK, eu
 * movo a arma e os bracos vao atras.
 *
 * @param shoulder junta do ombro (pai do braco)
 * @param elbow    junta do cotovelo
 * @param tx,ty,tz alvo do punho, no espaco do PAI do ombro (a espinha)
 * @param px,py,pz direcao do "polo": para onde o cotovelo deve apontar
 */
function solveLimb(shoulder, elbow, tx, ty, tz, px, py, pz, l1, l2) {
  _ikD.set(tx - shoulder.position.x, ty - shoulder.position.y, tz - shoulder.position.z);
  let dist = _ikD.length();
  if (dist < 1e-5) { _ikD.set(0, -1, 0); dist = l1; }
  else _ikD.multiplyScalar(1 / dist);

  // trava o alcance: alvo fora do alcance esticaria o osso e geraria NaN no acos
  const maxR = (l1 + l2) * 0.998;
  const minR = Math.abs(l1 - l2) + 0.03;
  if (dist > maxR) dist = maxR;
  else if (dist < minR) dist = minR;

  _ikPole.set(px, py, pz).normalize();
  _ikAxis.crossVectors(_ikD, _ikPole);
  if (_ikAxis.lengthSq() < 1e-8) _ikAxis.set(1, 0, 0);
  _ikAxis.normalize();

  let cosA = (l1 * l1 + dist * dist - l2 * l2) / (2 * l1 * dist);
  cosA = cosA < -1 ? -1 : (cosA > 1 ? 1 : cosA);
  let cosC = (l1 * l1 + l2 * l2 - dist * dist) / (2 * l1 * l2);
  cosC = cosC < -1 ? -1 : (cosC > 1 ? 1 : cosC);

  // gira a direcao do alvo em torno do eixo de dobra, em direcao ao polo
  _ikU.copy(_ikD).applyAxisAngle(_ikAxis, Math.acos(cosA));

  // base do ombro: X = eixo de dobra (eixo do cotovelo), Y = -bracoo (pende em -Y)
  _ikX.copy(_ikAxis);
  _ikY.copy(_ikU).multiplyScalar(-1);
  _ikZ.crossVectors(_ikX, _ikY).normalize();
  _ikX.crossVectors(_ikY, _ikZ).normalize();
  _ikM.makeBasis(_ikX, _ikY, _ikZ);
  shoulder.quaternion.setFromRotationMatrix(_ikM);
  elbow.rotation.set(-(Math.PI - Math.acos(cosC)), 0, 0);
}

/**
 * Quanto o quadril sobe (vertical do mundo) com o corpo deitado, para o joelho
 * e a mochila nao atravessarem o chao. Medido: sem isso o corpo afunda 0.43 m.
 */
const DEATH_LIFT = 0.24;

/** Pose alvo do corpo caido, ja em quaternion (o slerp da morte le daqui). */
function deathQ(x, y, z) {
  return new THREE.Quaternion().setFromEuler(new THREE.Euler(x, y, z));
}
const DEATH_POSE = {
  shoulderL: deathQ(-0.15, 0.10, 0.95),
  elbowL: deathQ(-0.42, 0, 0),
  shoulderR: deathQ(-0.05, -0.10, -1.05),
  elbowR: deathQ(-0.30, 0, 0),
  hipL: deathQ(-0.38, 0, -0.22),
  kneeL: deathQ(0.62, 0, 0),
  ankleL: deathQ(-0.35, 0, 0),
  hipR: deathQ(-0.12, 0, 0.30),
  kneeR: deathQ(0.42, 0, 0),
  ankleR: deathQ(-0.28, 0, 0)
};

/** Cache de geometria por uniforme. Vive a sessao inteira. */
const GEO_CACHE = new Map();

// ===========================================================================
// textura procedural do fardamento
// ===========================================================================

/**
 * POR QUE ESTA SECAO EXISTE.
 *
 * Ate aqui o inimigo era o unico objeto da cena SEM MAPA: cor chapada por
 * vertice, roughness constante, relevo zero - enquanto cada parede do cenario
 * tem albedo, normal e roughness proceduais. Era isso, e nao a geometria, que
 * o fazia ler como boneco de plastico dentro de um mundo com materiais.
 *
 * O que resolve A 25 METROS nao e o relevo (nenhum normal map sobrevive aquela
 * distancia depois do mipmap): e a MANCHA. Por isso o albedo do pano e um padrao
 * de camuflagem de quatro tons com blocos de ~15 cm - grande o bastante para
 * quebrar a cor unica de longe. A trama de sarja, a costura e o desgaste entram
 * no normal e no roughness, e sao o que segura a leitura de perto.
 *
 * Duas familias, porque pano e equipamento duro nao envelhecem igual:
 *   cloth - camuflagem + sarja + costura      (fardamento, colete, mochila)
 *   hard  - risco, arranhao e sujeira         (capacete, coturno, fuzil)
 *
 * O ruido e TILEAVEL (grade com indice em anel), o que deixa projetar as UV em
 * caixa por peca e repetir sem costura. Custo: ~100 ms UMA vez, durante o init
 * do BotManager, isto e, dentro da tela de carregamento. Zero por frame.
 */
const CLOTH_TEX = 512;
const HARD_TEX = 256;
/** Voltas de textura por metro: 1.6 -> padrao de 62 cm, mancha maior de ~15 cm. */
const UV_CLOTH = 1.6;
/** Equipamento duro: risco fino, padrao de 25 cm. */
const UV_HARD = 4.0;

let KIT = null;
let KIT_DONE = false;
let MATS = null;
/** Material unico das caixas de acerto (invisiveis). Vive a sessao inteira. */
let HITBOX_MAT = null;
/** Matriz de bind: identidade, e nunca muda (ver o construtor). */
const BIND_IDENTITY = new THREE.Matrix4();

function clamp255(v) { return v < 0 ? 0 : (v > 255 ? 255 : v | 0); }

/** Grade de ruido de valor. cells divide a textura, entao o resultado e tileavel. */
function lattice(cells, rnd) {
  const a = new Float32Array(cells * cells);
  for (let i = 0; i < a.length; i++) a[i] = rnd();
  return a;
}

function latAt(a, cells, u, v) {
  const x = u * cells, y = v * cells;
  let x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  x0 = ((x0 % cells) + cells) % cells;
  y0 = ((y0 % cells) + cells) % cells;
  const x1 = (x0 + 1) % cells, y1 = (y0 + 1) % cells;
  const r0 = a[y0 * cells + x0] * (1 - sx) + a[y0 * cells + x1] * sx;
  const r1 = a[y1 * cells + x0] * (1 - sx) + a[y1 * cells + x1] * sx;
  return r0 * (1 - sy) + r1 * sy;
}

function newCanvas(size) {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  return c;
}

function canvasTexture(canvas, srgb) {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Normal map a partir de um campo de altura, com gradiente em anel (tileavel). */
function writeNormal(g2d, hgt, N, strength) {
  const img = g2d.createImageData(N, N);
  const d = img.data;
  for (let y = 0; y < N; y++) {
    const yU = (y + N - 1) % N, yD = (y + 1) % N;
    for (let x = 0; x < N; x++) {
      const xL = (x + N - 1) % N, xR = (x + 1) % N;
      const dx = (hgt[y * N + xR] - hgt[y * N + xL]) * strength;
      const dy = (hgt[yD * N + x] - hgt[yU * N + x]) * strength;
      const inv = 1 / Math.sqrt(dx * dx + dy * dy + 1);
      const i = (y * N + x) * 4;
      d[i] = clamp255((-dx * inv * 0.5 + 0.5) * 255);
      d[i + 1] = clamp255((dy * inv * 0.5 + 0.5) * 255);
      d[i + 2] = clamp255((inv * 0.5 + 0.5) * 255);
      d[i + 3] = 255;
    }
  }
  g2d.putImageData(img, 0, 0);
}

/** Pano: camuflagem de 4 tons + sarja + costura. Base clara: a cor vem do vertice. */
function buildClothMaps() {
  const N = CLOTH_TEX;
  const rnd = mulberry32(0x9E3779B1);
  const L4 = lattice(4, rnd);
  const L8 = lattice(8, rnd);
  const L16 = lattice(16, rnd);
  const L64 = lattice(64, rnd);

  const cAlb = newCanvas(N), cNrm = newCanvas(N), cRgh = newCanvas(N);
  const gA = cAlb.getContext('2d'), gR = cRgh.getContext('2d');
  const alb = gA.createImageData(N, N);
  const rgh = gR.createImageData(N, N);
  const hgt = new Float32Array(N * N);

  for (let y = 0; y < N; y++) {
    const v = y / N;
    for (let x = 0; x < N; x++) {
      const u = x / N;
      const blob = 0.54 * latAt(L4, 4, u, v)
        + 0.31 * latAt(L8, 8, u, v)
        + 0.15 * latAt(L16, 16, u, v);
      // ruido fino na ENTRADA do degrau: borda de mancha recortada, nao circulo
      const t = blob + (latAt(L64, 64, u, v) - 0.5) * 0.12;

      // 4 tons, faixa de ~2.3:1 em linear - e o contraste de uma camuflagem real.
      // Mais que isso vira estampa; menos volta a ser cor chapada.
      let tone, tilt;
      if (t < 0.40) { tone = 0.660; tilt = -0.050; }
      else if (t < 0.56) { tone = 0.800; tilt = -0.014; }
      else if (t < 0.76) { tone = 0.895; tilt = 0.012; }
      else { tone = 0.965; tilt = 0.034; }

      const twill = ((x + (y << 1)) % 6) < 3 ? 1.0 : 0.968;
      const seam = ((y + 37) % 149 < 2) || ((x + 61) % 181 < 2);
      const grime = 0.93 + 0.08 * latAt(L8, 8, u + 0.37, v + 0.12);
      const a = tone * twill * grime * (seam ? 0.945 : 1);

      const i = (y * N + x) * 4;
      alb.data[i] = clamp255(a * (1 + tilt) * 255);
      alb.data[i + 1] = clamp255(a * 255);
      alb.data[i + 2] = clamp255(a * (1 - tilt * 1.35) * 255);
      alb.data[i + 3] = 255;

      // pano quase fosco; as partes gastas ganham um brilho de uso
      const r = 0.960 - (t - 0.5) * 0.09 - (twill < 1 ? 0.02 : 0)
        - 0.15 * latAt(L16, 16, u + 0.61, v + 0.28);
      rgh.data[i] = rgh.data[i + 1] = rgh.data[i + 2] = clamp255(r * 255);
      rgh.data[i + 3] = 255;

      hgt[y * N + x] = (twill < 1 ? 0.30 : 0.62) + (t - 0.5) * 0.22 + (seam ? 0.55 : 0);
    }
  }

  gA.putImageData(alb, 0, 0);
  gR.putImageData(rgh, 0, 0);
  writeNormal(cNrm.getContext('2d'), hgt, N, 2.4);

  return {
    map: canvasTexture(cAlb, true),
    normalMap: canvasTexture(cNrm, false),
    roughnessMap: canvasTexture(cRgh, false)
  };
}

/** Desenha a mesma figura nas 9 celulas vizinhas: a textura fecha nas bordas. */
function tile9(draw, x, y, N) {
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) draw(x + ox * N, y + oy * N);
  }
}

function blob2d(g, x, y, r, rgb, a) {
  if (r <= 0) return;
  const grd = g.createRadialGradient(x, y, 0, x, y, r);
  grd.addColorStop(0, `rgba(${rgb},${a})`);
  grd.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grd;
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.fill();
}

function line2d(g, x0, y0, x1, y1, w, rgb, a) {
  g.strokeStyle = `rgba(${rgb},${a})`;
  g.lineWidth = w;
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
}

/** Equipamento duro: risco de metal exposto (liso) sobre sujeira acumulada (rugosa). */
function buildHardMaps() {
  const N = HARD_TEX;
  const rnd = mulberry32(0x2545F491);
  const cAlb = newCanvas(N), cNrm = newCanvas(N), cRgh = newCanvas(N);
  const gA = cAlb.getContext('2d');
  const gR = cRgh.getContext('2d');

  gA.fillStyle = '#eae8e5'; gA.fillRect(0, 0, N, N);
  gR.fillStyle = '#a8a8a8'; gR.fillRect(0, 0, N, N);

  for (let i = 0; i < 30; i++) {
    const x = rnd() * N, y = rnd() * N;
    const r = N * (0.04 + rnd() * 0.15);
    const a = (0.08 + rnd() * 0.12).toFixed(3);
    tile9((px, py) => {
      blob2d(gA, px, py, r, '46,42,36', a);
      blob2d(gR, px, py, r, '240,240,240', a);
    }, x, y, N);
  }

  gA.lineCap = 'round';
  gR.lineCap = 'round';
  for (let i = 0; i < 150; i++) {
    const x = rnd() * N, y = rnd() * N;
    const ang = rnd() * TAU;
    const len = N * (0.02 + rnd() * 0.15);
    const dx = Math.cos(ang) * len, dy = Math.sin(ang) * len;
    const w = rnd() < 0.75 ? 1 : 2;
    const a = (0.28 + rnd() * 0.42).toFixed(3);
    tile9((px, py) => {
      line2d(gA, px, py, px + dx, py + dy, w, '255,252,246', a);
      line2d(gR, px, py, px + dx, py + dy, w, '54,54,54', a);
    }, x, y, N);
  }

  // altura invertida: o risco e SULCO, nao crista
  const src = gA.getImageData(0, 0, N, N).data;
  const hgt = new Float32Array(N * N);
  for (let i = 0, p = 0; i < hgt.length; i++, p += 4) {
    hgt[i] = 1 - (src[p] * 0.35 + src[p + 1] * 0.5 + src[p + 2] * 0.15) / 255;
  }
  writeNormal(cNrm.getContext('2d'), hgt, N, 1.5);

  return {
    map: canvasTexture(cAlb, true),
    normalMap: canvasTexture(cNrm, false),
    roughnessMap: canvasTexture(cRgh, false)
  };
}

/** Gera o conjunto uma unica vez. Falha (sem canvas) degrada para cor chapada. */
function getKit() {
  if (KIT_DONE) return KIT;
  KIT_DONE = true;
  try {
    if (typeof document === 'undefined' || !document.createElement) throw new Error('sem canvas 2D');
    KIT = { cloth: buildClothMaps(), hard: buildHardMaps() };
  } catch (err) {
    KIT = null;
    console.warn('[BotModel] textura procedural indisponivel; inimigo fica em cor chapada', err);
  }
  return KIT;
}

function applyKit(mat, set, normalStrength) {
  if (!set) return;
  mat.map = set.map;
  mat.normalMap = set.normalMap;
  mat.roughnessMap = set.roughnessMap;
  mat.normalScale.set(normalStrength, normalStrength);
  mat.needsUpdate = true;
}

/**
 * TRES materiais - e o numero de draw calls do bot, entao aqui cada material
 * cobrado e uma decisao, nao um detalhe.
 *
 * Antes eram quatro, e sair de 14 malhas rigidas para SkinnedMesh inverteu a
 * conta: com malha por parte, material extra era de graca (cada parte ja era uma
 * chamada); com o corpo mesclado, cada material a mais parte a malha de novo. O
 * que sobrevive e o que muda a LEITURA da superficie:
 *   cloth  - pano fosco, camuflagem, sem brilho          (fardamento e colete)
 *   gear   - equipamento duro riscado, semi-brilhante    (capacete e coturno)
 *   weapon - metal escuro do fuzil, o unico metalico
 * O coturno foi para `gear` porque compartilha a MESMA familia de textura (hard):
 * o que se perdia era so o multiplicador de cor, e esse voltou por cor de vertice
 * (BOOT_ALBEDO_FIX). O capacete, que e o que o jogador ve primeiro, ficou com os
 * valores originais intactos.
 */
function getTemplates() {
  if (MATS) return MATS;
  const kit = getKit();

  const cloth = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 1.0, metalness: 0.0,
    envMapIntensity: 0.85, flatShading: false
  });
  const gear = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.95, metalness: 0.06, envMapIntensity: 0.80
  });
  const weapon = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.75, metalness: 0.42, envMapIntensity: 0.75
  });

  if (kit) {
    applyKit(cloth, kit.cloth, 0.75);
    applyKit(gear, kit.hard, 0.85);
    applyKit(weapon, kit.hard, 0.80);
  } else {
    // sem mapa, ao menos o valor de rugosidade separa as classes
    cloth.roughness = 0.90;
    gear.roughness = 0.62;
    weapon.roughness = 0.50;
  }

  MATS = { cloth, gear, weapon };
  return MATS;
}

/**
 * Projecao em caixa: recalcula a UV de cada vertice a partir da posicao ja
 * POSICIONADA na peca, escolhendo o plano pelo eixo dominante da normal.
 *
 * Sem isto a densidade de textura era ingovernavel: a UV nativa de cada
 * primitiva vai de 0 a 1 na propria face, entao a mesma camuflagem apareceria
 * com 1.3 m no tronco e 7 cm num porta-carregador. Com a projecao, o padrao tem
 * o MESMO tamanho em metros no corpo inteiro - e, como a UV sai da posicao, cada
 * peca cai num pedaco diferente da textura e a repeticao some de graca.
 */
function boxUV(geo, scale) {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  const uv = geo.getAttribute('uv');
  if (!pos || !nor || !uv) return geo;
  for (let i = 0; i < pos.count; i++) {
    const px = pos.getX(i), py = pos.getY(i), pz = pos.getZ(i);
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    let u, v;
    if (ny >= nx && ny >= nz) { u = px; v = pz; }
    else if (nx >= nz) { u = pz; v = py; }
    else { u = px; v = py; }
    uv.setXY(i, u * scale, v * scale);
  }
  uv.needsUpdate = true;
  return geo;
}

// ===========================================================================
// utilitarios de construcao
// ===========================================================================

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rnd() {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pinta a geometria inteira com uma cor de vertice (hex em sRGB). */
function paint(geo, hex) {
  _col.setHex(hex);
  const count = geo.getAttribute('position').count;
  const arr = new Float32Array(count * 3);
  for (let i = 0, o = 0; i < count; i++) {
    arr[o++] = _col.r; arr[o++] = _col.g; arr[o++] = _col.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geo;
}

/** Posiciona/rotaciona a geometria no espaco da parte e pinta. */
function at(geo, hex, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) {
  if (rx || ry || rz) {
    _eu.set(rx, ry, rz);
    _m4.makeRotationFromEuler(_eu);
  } else {
    _m4.identity();
  }
  _m4.setPosition(x, y, z);
  geo.applyMatrix4(_m4);
  return paint(geo, hex);
}

/**
 * Bloco com afunilamento: cilindro de 4 lados girado 45 graus vira uma caixa
 * que pode ter topo e base de larguras diferentes. E o truque que da forma de
 * tronco (ombro largo, cintura estreita) sem custo de modelagem.
 */
function taper(wTop, wBottom, h, depthRatio) {
  const g = new THREE.CylinderGeometry(wTop * HALF_SQ2, wBottom * HALF_SQ2, h, 4, 1);
  g.rotateY(Math.PI / 4);
  g.scale(1, 1, depthRatio);
  return g;
}

function scaled(geo, sx, sy, sz) {
  geo.scale(sx, sy, sz);
  return geo;
}

function merge(list, fallbackHex, uvScale) {
  const clean = list.filter(Boolean);
  if (clean.length === 0) {
    return boxUV(paint(new THREE.BoxGeometry(0.1, 0.1, 0.1), fallbackHex || 0x808080), uvScale || UV_CLOTH);
  }
  let out = null;
  try {
    out = mergeGeometries(clean, false);
  } catch (err) {
    out = null;
  }
  if (!out) {
    // fallback: usa a maior peca em vez de quebrar a construcao do bot
    out = clean[0];
    for (let i = 1; i < clean.length; i++) clean[i].dispose();
  } else {
    for (const g of clean) g.dispose();
  }
  // a UV e reprojetada DEPOIS da mescla: uma passada so, na peca inteira
  boxUV(out, uvScale || UV_CLOTH);
  out.computeBoundingSphere();
  out.computeBoundingBox();
  return out;
}

// ===========================================================================
// pele: uma geometria por familia de material, com o corpo inteiro dentro
// ===========================================================================

/**
 * Copia a peca ja pronta e amarra TODOS os seus vertices a um unico osso.
 *
 * Peso 1 num osso so - de proposito. O corpo era feito de partes rigidas
 * penduradas nas juntas, e um vertice com peso 1 num osso reproduz EXATAMENTE
 * aquilo: mesma silhueta, mesma dobra, mesma pose de morte, zero deformacao nova
 * para o revisor visual reprovar. O ganho e so de custo - 1 malha em vez de 14.
 * Skinning suave (peso dividido em dois ossos) mudaria o desenho do cotovelo e do
 * joelho, e o vao de junta ja esta resolvido por geometria (ver buildForeArm).
 */
function skinnedCopy(geo, boneIndex) {
  const g = geo.clone();
  const count = g.getAttribute('position').count;
  const idx = new Uint16Array(count * 4);
  const wgt = new Float32Array(count * 4);
  for (let i = 0, o = 0; i < count; i++, o += 4) {
    idx[o] = boneIndex;
    wgt[o] = 1;
  }
  g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(idx, 4));
  g.setAttribute('skinWeight', new THREE.BufferAttribute(wgt, 4));
  return g;
}

/**
 * Junta varias pecas numa geometria de esqueleto. `list` vem em pares
 * (geometria, indice do osso).
 *
 * O vertice NAO e transformado para o espaco do corpo: fica no espaco da propria
 * peca, como estava quando a peca era filha da junta. Isso funciona porque o
 * esqueleto e criado com as inversas de bind todas IDENTIDADE (ver o construtor),
 * e ai o shader calcula osso.matrixWorld * vertice - exatamente a conta que o
 * three fazia para uma malha filha daquela junta.
 */
function mergeSkin(list) {
  const clones = [];
  for (let i = 0; i < list.length; i += 2) {
    const geo = list[i];
    if (geo) clones.push(skinnedCopy(geo, list[i + 1]));
  }
  let out = null;
  try {
    out = mergeGeometries(clones, false);
  } catch (err) {
    out = null;
  }
  if (!out) {
    // fallback: melhor um bot incompleto do que um bot que nao nasce
    out = clones[0];
    for (let i = 1; i < clones.length; i++) clones[i].dispose();
    console.warn('[BotModel] falha ao mesclar a pele do inimigo; corpo parcial');
  } else {
    for (const g of clones) g.dispose();
  }
  // volume escrito a mao: o calculado nao vale nada aqui (ver BODY_RADIUS)
  out.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, BODY_CENTER_Y, 0), BODY_RADIUS);
  out.boundingBox = new THREE.Box3(
    new THREE.Vector3(-BODY_RADIUS, BODY_CENTER_Y - BODY_RADIUS, -BODY_RADIUS),
    new THREE.Vector3(BODY_RADIUS, BODY_CENTER_Y + BODY_RADIUS, BODY_RADIUS)
  );
  return out;
}

/**
 * Caixa que substitui a peca como alvo de bala.
 *
 * O TargetRegistry le UMA coisa da hitbox: `geometry.boundingBox` (secao 4.11 -
 * ele monta um OBB no espaco local do objeto). Entao uma caixa com o mesmo
 * bounding box da peca produz o MESMO acerto que a peca produzia, com 24
 * vertices em vez de milhares e sem nunca ser desenhada.
 */
function boxProxy(geo) {
  if (!geo.boundingBox) geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const sx = Math.max(1e-3, bb.max.x - bb.min.x);
  const sy = Math.max(1e-3, bb.max.y - bb.min.y);
  const sz = Math.max(1e-3, bb.max.z - bb.min.z);
  const g = new THREE.BoxGeometry(sx, sy, sz);
  g.translate(
    (bb.min.x + bb.max.x) * 0.5,
    (bb.min.y + bb.max.y) * 0.5,
    (bb.min.z + bb.max.z) * 0.5
  );
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return g;
}

/** Multiplica a cor de vertice ja gravada (usado no coturno, ver BOOT_ALBEDO_FIX). */
function scaleVertexColor(geo, k) {
  const col = geo.getAttribute('color');
  if (!col) return geo;
  const arr = col.array;
  for (let i = 0; i < arr.length; i++) arr[i] *= k;
  col.needsUpdate = true;
  return geo;
}

/**
 * Material das caixas de acerto. Nunca chega ao shader (a malha e invisivel),
 * mas Mesh sem material quebra o clone/dispose do three - entao existe um so,
 * compartilhado pelo pool inteiro.
 */
function getHitboxMaterial() {
  if (!HITBOX_MAT) {
    HITBOX_MAT = new THREE.MeshBasicMaterial({ visible: false });
    HITBOX_MAT.name = 'botHitbox';
  }
  return HITBOX_MAT;
}

/** Nem a caixa de acerto nem a pele participam de raycast de cena (mira, cenario). */
function noBotRaycast() { /* proposital: nada */ }

// ===========================================================================
// as 8 pecas do corpo
// ===========================================================================

function buildHead(c) {
  const p = [];
  // pescoco e cabeca
  p.push(at(new THREE.CylinderGeometry(0.052, 0.060, 0.11, 8), c.skin, 0, 0.04, 0));
  p.push(at(scaled(new THREE.SphereGeometry(0.098, 14, 10), 1.0, 1.14, 1.05), c.skin, 0, 0.146, 0.004));
  p.push(at(new THREE.BoxGeometry(0.132, 0.088, 0.088), c.skin, 0, 0.104, 0.052));
  // capacete: calota + aba
  p.push(at(scaled(new THREE.SphereGeometry(0.126, 16, 9, 0, TAU, 0, Math.PI * 0.62), 1.04, 0.96, 1.10), c.helmet, 0, 0.136, 0.002));
  p.push(at(new THREE.CylinderGeometry(0.132, 0.136, 0.030, 16, 1, true), c.helmet, 0, 0.108, 0.002));
  /**
   * OS "OLHOS VERMELHOS". Aqui havia um anel de identificacao de raio 0.130-0.133
   * a 0.165 de altura. A calota do capacete, naquela altura, tem meia-largura
   * 0.127 em X e 0.134 em Z: o anel ficava ESCONDIDO na frente e atras, e furava
   * a calota exatamente NOS DOIS LADOS. O resultado eram dois pontos vermelhos
   * simetricos na altura da cabeca - a revisao os leu como olhos, com razao.
   * Trocado por uma chapa na nuca, que fica dentro da silhueta e nao pareia.
   */
  p.push(at(new THREE.BoxGeometry(0.054, 0.028, 0.016), c.mark, 0, 0.168, -0.132));
  // trilhos laterais, fones e suporte de visao noturna
  p.push(at(new THREE.BoxGeometry(0.014, 0.050, 0.120), c.metal, 0.126, 0.140, 0.010));
  p.push(at(new THREE.BoxGeometry(0.014, 0.050, 0.120), c.metal, -0.126, 0.140, 0.010));
  p.push(at(new THREE.CylinderGeometry(0.047, 0.047, 0.034, 10), c.pad, 0.118, 0.106, 0.002, 0, 0, Math.PI / 2));
  p.push(at(new THREE.CylinderGeometry(0.047, 0.047, 0.034, 10), c.pad, -0.118, 0.106, 0.002, 0, 0, Math.PI / 2));
  p.push(at(new THREE.BoxGeometry(0.052, 0.044, 0.038), c.metal, 0, 0.198, 0.086));
  // oculos / visor
  p.push(at(new THREE.BoxGeometry(0.184, 0.040, 0.030), c.lens, 0, 0.138, 0.080));
  p.push(at(new THREE.BoxGeometry(0.192, 0.016, 0.026), c.gear, 0, 0.162, 0.076));
  // microfone
  p.push(at(new THREE.CylinderGeometry(0.005, 0.005, 0.075, 6), c.gear, 0.086, 0.098, 0.048, 0, 0, -0.9));
  return merge(p, c.helmet, UV_HARD);
}

function buildTorso(c) {
  const p = [];
  // tronco afunilado
  p.push(at(taper(0.355, 0.298, 0.375, 0.60), c.cloth, 0, 0.19, 0));
  // colete balistico por cima
  p.push(at(taper(0.368, 0.320, 0.290, 0.68), c.vest, 0, 0.205, 0.004));
  p.push(at(new THREE.BoxGeometry(0.330, 0.080, 0.230), c.vest, 0, 0.055, 0.004));
  p.push(at(new THREE.BoxGeometry(0.300, 0.250, 0.052), c.vest, 0, 0.215, -0.098));
  // alcas de ombro (a marca de unidade migrou do capacete para ca: fica na
  // silhueta do ombro, que e o que o jogador ve primeiro, sem virar par de olhos)
  p.push(at(new THREE.BoxGeometry(0.078, 0.105, 0.160), c.gear, 0.106, 0.332, 0.008));
  p.push(at(new THREE.BoxGeometry(0.078, 0.105, 0.160), c.gear, -0.106, 0.332, 0.008));
  p.push(at(new THREE.BoxGeometry(0.070, 0.014, 0.104), c.mark, -0.106, 0.386, 0.008));
  // gola
  p.push(at(new THREE.CylinderGeometry(0.076, 0.084, 0.078, 10), c.clothDark, 0, 0.398, 0));
  // porta-carregadores no peito
  for (let i = -1; i <= 1; i++) {
    p.push(at(new THREE.BoxGeometry(0.070, 0.108, 0.055), c.gear, i * 0.076, 0.142, 0.118));
  }
  p.push(at(new THREE.BoxGeometry(0.086, 0.092, 0.050), c.gear, 0.118, 0.250, 0.104));
  // radio e antena
  p.push(at(new THREE.BoxGeometry(0.056, 0.118, 0.046), c.gear, 0.136, 0.300, -0.078));
  p.push(at(new THREE.CylinderGeometry(0.006, 0.004, 0.230, 5), c.metal, 0.136, 0.470, -0.078, 0.16, 0, 0.08));
  // mochila e saco de dormir
  p.push(at(taper(0.255, 0.235, 0.300, 0.58), c.clothDark, 0, 0.195, -0.162));
  p.push(at(new THREE.BoxGeometry(0.238, 0.062, 0.135), c.gear, 0, 0.348, -0.158));
  p.push(at(new THREE.CylinderGeometry(0.046, 0.046, 0.255, 10), c.clothDark, 0, 0.048, -0.188, 0, 0, Math.PI / 2));
  p.push(at(new THREE.BoxGeometry(0.020, 0.290, 0.020), c.gear, 0.090, 0.195, -0.230));
  p.push(at(new THREE.BoxGeometry(0.020, 0.290, 0.020), c.gear, -0.090, 0.195, -0.230));
  return merge(p, c.cloth, UV_CLOTH);
}

function buildPelvis(c) {
  const p = [];
  p.push(at(taper(0.300, 0.278, 0.265, 0.72), c.cloth, 0, 0.005, 0));
  p.push(at(new THREE.BoxGeometry(0.316, 0.056, 0.222), c.gear, 0, 0.100, 0));
  p.push(at(new THREE.BoxGeometry(0.108, 0.120, 0.078), c.gear, -0.140, 0.020, -0.062));
  p.push(at(new THREE.BoxGeometry(0.074, 0.150, 0.062), c.gear, 0.152, -0.040, 0.028, 0, 0, 0.10));
  p.push(at(new THREE.BoxGeometry(0.030, 0.100, 0.052), c.metal, -0.152, -0.010, 0.056));
  p.push(at(new THREE.BoxGeometry(0.090, 0.078, 0.048), c.gear, 0.060, 0.030, 0.112));
  return merge(p, c.cloth, UV_CLOTH);
}

/**
 * JUNTAS FECHADAS.
 *
 * Cotovelo e joelho eram um VAO: o osso de cima terminava numa calota de capsula
 * exatamente no eixo da junta (ou 3 cm antes dele, no caso da coxa) e o de baixo
 * comecava logo abaixo. Com o membro esticado quase nao aparecia; dobrado, dava
 * para ver o cenario ATRAVES do cotovelo e do joelho.
 *
 * A cura tem duas partes e as duas sao necessarias:
 *   1. a capsula do osso PAI passa do eixo da junta, em vez de parar nele;
 *   2. o osso FILHO ganha uma esfera centrada em (0,0,0), ou seja, no proprio
 *      eixo de rotacao - assim ela preenche a dobra em QUALQUER angulo, sem
 *      depender da pose, e ainda funciona como cotoveleira/joelheira.
 */
function buildUpperArm(c) {
  const p = [];
  p.push(at(scaled(new THREE.SphereGeometry(0.072, 12, 8), 1.0, 0.94, 1.0), c.vest, 0, -0.012, 0));
  // capsula ate -0.2975: passa do cotovelo (-0.285) em vez de morrer nele
  p.push(at(new THREE.CapsuleGeometry(0.055, 0.175, 4, 10), c.cloth, 0, -0.155, 0));
  p.push(at(new THREE.CylinderGeometry(0.058, 0.058, 0.038, 10, 1, true), c.mark, 0, -0.100, 0));
  return merge(p, c.cloth, UV_CLOTH);
}

function buildForeArm(c) {
  const p = [];
  // esfera NO EIXO do cotovelo: fecha a dobra em qualquer angulo
  p.push(at(new THREE.SphereGeometry(0.058, 10, 8), c.pad, 0, 0, 0));
  p.push(at(new THREE.CapsuleGeometry(0.048, 0.150, 4, 10), c.clothDark, 0, -0.128, 0));
  // cotoveleira em ANEL, nao em placa de um lado so: a base que a IK monta no
  // braco fica girada, e placa lateral pode nascer virada para dentro do corpo
  p.push(at(new THREE.CylinderGeometry(0.059, 0.055, 0.072, 10), c.pad, 0, -0.034, 0));
  p.push(at(new THREE.CylinderGeometry(0.053, 0.056, 0.042, 10), c.gear, 0, -0.090, 0));
  p.push(at(new THREE.BoxGeometry(0.070, 0.098, 0.066), c.glove, 0, -0.262, 0.010));
  p.push(at(new THREE.BoxGeometry(0.052, 0.058, 0.062), c.glove, 0, -0.318, 0.030, 0.35, 0, 0));
  return merge(p, c.clothDark, UV_CLOTH);
}

function buildThigh(c) {
  const p = [];
  // capsula ate -0.461: cobre o joelho (-0.445). Antes parava em -0.4125 e
  // sobravam 3 cm de buraco entre coxa e canela ja na pose parada.
  p.push(at(new THREE.CapsuleGeometry(0.084, 0.290, 4, 10), c.cloth, 0, -0.232, 0));
  // bolso de perna: no eixo da IK o +Z local aponta para TRAS, entao a peca que
  // deve ficar na frente/lado de fora e autorada em -Z (ver buildFoot)
  p.push(at(new THREE.BoxGeometry(0.086, 0.090, 0.052), c.gear, 0.052, -0.170, -0.062));
  p.push(at(new THREE.BoxGeometry(0.074, 0.048, 0.044), c.gear, -0.050, -0.252, -0.056));
  return merge(p, c.cloth, UV_CLOTH);
}

/** Canela: da rotula ate o cano da bota. O pe e separado (tem tornozelo). */
function buildShin(c) {
  const p = [];
  // rotula no EIXO do joelho + joelheira na frente (-Z local, ver buildFoot)
  p.push(at(new THREE.SphereGeometry(0.078, 12, 8), c.pad, 0, 0, 0));
  p.push(at(new THREE.BoxGeometry(0.108, 0.096, 0.050), c.pad, 0, -0.022, -0.052));
  p.push(at(new THREE.CapsuleGeometry(0.062, 0.180, 4, 10), c.cloth, 0, -0.168, 0));
  p.push(at(new THREE.BoxGeometry(0.098, 0.130, 0.112), c.boot, 0, -0.312, -0.006));
  p.push(at(new THREE.BoxGeometry(0.104, 0.036, 0.104), c.gear, 0, -0.246, -0.006));
  return merge(p, c.cloth, UV_CLOTH);
}

/**
 * Pe, com origem NO TORNOZELO (7.5 cm do chao). Existir como peca propria e o
 * que permite manter a sola paralela ao chao com a perna dobrada - sem isso o
 * bot agachado fica na ponta do pe ou enterra a bota no asfalto.
 *
 * ATENCAO AO SINAL DE Z. A base que solveLimb monta e
 * X = cross(direcao, polo), Y = -osso, Z = X x Y - o que deixa o +Z LOCAL do
 * quadril, do joelho e do tornozelo apontando para TRAS do bot (medido: com o
 * corpo parado, o +Z do tornozelo vale (-0.21, 0, -0.98) no mundo). E a mesma
 * rotacao de 180 graus em Y que o codigo da morte ja tinha notado ao explicar
 * por que interpola em quaternion. Consequencia: a bota, autorada com a ponta em
 * +Z, saia apontando PARA TRAS - o soldado andava com os pes ao contrario.
 * Corrigido aqui, no unico lugar barato: a peca e autorada em -Z. Mexer na base
 * da IK arrastaria o dobramento, o nivelamento da sola e a pose de morte junto.
 */
function buildFoot(c) {
  const p = [];
  p.push(at(new THREE.BoxGeometry(0.102, 0.072, 0.212), c.boot, 0, -0.028, -0.042));
  p.push(at(new THREE.BoxGeometry(0.108, 0.028, 0.232), c.pad, 0, -0.060, -0.046));
  p.push(at(new THREE.BoxGeometry(0.096, 0.050, 0.062), c.boot, 0, -0.010, 0.058));
  return merge(p, c.boot, UV_HARD);
}

/** Fuzil compacto. Aponta para +Z, boca do cano em z = 0.515. */
function buildWeapon(c) {
  const p = [];
  p.push(at(new THREE.BoxGeometry(0.055, 0.086, 0.300), c.weapon, 0, 0, 0.010));
  p.push(at(new THREE.BoxGeometry(0.042, 0.018, 0.350), c.metal, 0, 0.052, 0.060));
  p.push(at(new THREE.BoxGeometry(0.048, 0.058, 0.215), c.weaponDark, 0, 0.006, 0.240));
  p.push(at(new THREE.CylinderGeometry(0.013, 0.013, 0.190, 8), c.metal, 0, 0.006, 0.382, Math.PI / 2, 0, 0));
  p.push(at(new THREE.CylinderGeometry(0.020, 0.017, 0.058, 8), c.metal, 0, 0.006, 0.486, Math.PI / 2, 0, 0));
  p.push(at(new THREE.BoxGeometry(0.030, 0.046, 0.042), c.metal, 0, 0.032, 0.336));
  p.push(at(new THREE.BoxGeometry(0.032, 0.165, 0.070), c.weaponDark, 0, -0.104, -0.006, -0.16, 0, 0));
  p.push(at(new THREE.BoxGeometry(0.034, 0.100, 0.048), c.weaponDark, 0, -0.072, -0.102, 0.30, 0, 0));
  p.push(at(new THREE.BoxGeometry(0.018, 0.042, 0.062), c.metal, 0, -0.040, -0.046));
  p.push(at(new THREE.CylinderGeometry(0.019, 0.019, 0.140, 8), c.metal, 0, 0.004, -0.212, Math.PI / 2, 0, 0));
  p.push(at(new THREE.BoxGeometry(0.042, 0.086, 0.150), c.weaponDark, 0, -0.012, -0.276));
  p.push(at(new THREE.BoxGeometry(0.050, 0.026, 0.120), c.weaponDark, 0, 0.038, -0.268));
  p.push(at(new THREE.BoxGeometry(0.032, 0.044, 0.086), c.weapon, 0, 0.090, 0.062));
  p.push(at(new THREE.BoxGeometry(0.028, 0.030, 0.052), c.metal, 0, 0.062, 0.062));
  p.push(at(new THREE.CylinderGeometry(0.017, 0.017, 0.012, 10), c.lens, 0, 0.090, 0.106, Math.PI / 2, 0, 0));
  return merge(p, c.weapon, UV_HARD);
}

/**
 * Monta (uma vez por uniforme) tudo o que os bots daquele uniforme compartilham:
 *   cloth  - SkinnedMesh do pano: torso, quadril, bracos e pernas
 *   hard   - SkinnedMesh do equipamento duro: cabeca/capacete e os dois coturnos
 *   weapon - fuzil, malha rigida filha do pivo da arma
 *   hit    - caixas de acerto, uma por peca (invisiveis)
 * As pecas cruas sao liberadas no fim: quem fica na memoria e o resultado.
 */
function getVariantGeometry(variant) {
  let set = GEO_CACHE.get(variant);
  if (set) return set;
  const c = UNIFORMS[variant];

  const parts = {
    head: buildHead(c),
    torso: buildTorso(c),
    pelvis: buildPelvis(c),
    upperArm: buildUpperArm(c),
    foreArm: buildForeArm(c),
    thigh: buildThigh(c),
    shin: buildShin(c),
    foot: buildFoot(c)
  };

  // a caixa de acerto sai da peca ANTES de qualquer ajuste de cor
  const hit = {};
  for (const key of Object.keys(parts)) hit[key] = boxProxy(parts[key]);

  scaleVertexColor(parts.foot, BOOT_ALBEDO_FIX);

  set = {
    cloth: mergeSkin([
      parts.torso, BONE.spine,
      parts.pelvis, BONE.hips,
      parts.upperArm, BONE.shoulderL,
      parts.upperArm, BONE.shoulderR,
      parts.foreArm, BONE.elbowL,
      parts.foreArm, BONE.elbowR,
      parts.thigh, BONE.hipL,
      parts.thigh, BONE.hipR,
      parts.shin, BONE.kneeL,
      parts.shin, BONE.kneeR
    ]),
    hard: mergeSkin([
      parts.head, BONE.neck,
      parts.foot, BONE.ankleL,
      parts.foot, BONE.ankleR
    ]),
    weapon: buildWeapon(c),
    hit
  };

  for (const key of Object.keys(parts)) parts[key].dispose();

  GEO_CACHE.set(variant, set);
  return set;
}

/**
 * Chamado no dispose do BotManager: libera geometria, textura e material
 * compartilhados. Tudo aqui e de sessao inteira, entao so sai por este caminho.
 */
export function disposeSharedBotGeometry() {
  for (const set of GEO_CACHE.values()) {
    set.cloth?.dispose?.();
    set.hard?.dispose?.();
    set.weapon?.dispose?.();
    if (set.hit) {
      for (const key of Object.keys(set.hit)) set.hit[key].dispose?.();
    }
  }
  GEO_CACHE.clear();

  if (HITBOX_MAT) { HITBOX_MAT.dispose(); HITBOX_MAT = null; }

  if (MATS) {
    for (const key of Object.keys(MATS)) MATS[key].dispose?.();
    MATS = null;
  }
  if (KIT) {
    for (const fam of Object.keys(KIT)) {
      const set = KIT[fam];
      for (const key of Object.keys(set)) set[key].dispose?.();
    }
    KIT = null;
  }
  KIT_DONE = false;
}

// ===========================================================================
// modelo
// ===========================================================================

export class BotModel {
  constructor(variant = 0, seed = 1) {
    this.variant = ((variant % UNIFORMS.length) + UNIFORMS.length) % UNIFORMS.length;
    this.uniform = UNIFORMS[this.variant];
    const rnd = mulberry32(seed * 2654435761);
    this.rnd = rnd;

    const geo = getVariantGeometry(this.variant);

    /**
     * Deriva de cor por bot: dois soldados do mesmo pelotao nunca sao identicos.
     *
     * Os multiplicadores passam de 1.0 DE PROPOSITO. O albedo agora entra na
     * conta (cor final = multiplicador x cor-do-vertice x mapa), e o mapa tem
     * media medida de 0.60 em linear no pano e ~0.74 no equipamento duro (por
     * isso 1.55 x 0.60 = 0.93, exatamente o brilho medio de antes). Sem compensar,
     * o mapa deixaria de MODULAR e passaria a ESCURECER - o soldado sumiria na
     * sombra. Com a compensacao, quem define o valor continua sendo a paleta, e
     * o mapa so espalha a mancha em torno dela. Sao dois fatores porque as duas
     * familias de textura tem media diferente.
     */
    const T = getTemplates();
    const dev = 0.96 + rnd() * 0.12;
    const warm = (rnd() - 0.5) * 0.05;
    const kCloth = 1.55 * dev;
    const kHard = 1.20 * dev;

    this.clothMat = T.cloth.clone();
    this.clothMat.color.setRGB(kCloth * (1 + warm), kCloth, kCloth * (1 - warm * 0.7));
    this.clothMat.roughness = Math.min(1, T.cloth.roughness * (0.93 + rnd() * 0.08));

    this.gearMat = T.gear.clone();
    this.gearMat.color.setRGB(kHard * 0.97, kHard * 0.97, kHard * 0.99);
    this.gearMat.roughness = Math.min(1, T.gear.roughness * (0.92 + rnd() * 0.14));

    // Fuzil: era cor quase branca (0.92-1.04) com metalness 0.62 e envMap cheio -
    // contra o ceu do fim de tarde isso vira um bloco reflexivo sem detalhe. Agora
    // e escuro, mais rugoso, menos metalico, e o risco de metal exposto vem do
    // mapa em vez de vir do reflexo do ceu inteiro.
    this.weaponMat = T.weapon.clone();
    this.weaponMat.color.setRGB(kHard * 0.80, kHard * 0.82, kHard * 0.85);

    // nome historico preservado: o resto do arquivo fala em bodyMat
    this.bodyMat = this.clothMat;

    // --- esqueleto -------------------------------------------------------
    // As juntas eram Group; agora sao Bone. Bone E um Object3D - position,
    // rotation e quaternion funcionam igual, e por isso toda a animacao
    // procedural (IK, ciclo de passo, morte) continua exatamente como estava.
    this.root = new THREE.Group();
    this.root.name = 'bot';

    this.hips = new THREE.Bone();
    this.hips.position.y = HIP_Y;
    this.root.add(this.hips);

    this.spine = new THREE.Bone();
    this.spine.position.y = SPINE_Y;
    this.hips.add(this.spine);

    this.neck = new THREE.Bone();
    this.neck.position.y = NECK_Y;
    this.spine.add(this.neck);

    this.shoulderL = new THREE.Bone();
    this.shoulderL.position.set(SHOULDER_X, SHOULDER_Y, 0);
    this.spine.add(this.shoulderL);
    this.elbowL = new THREE.Bone();
    this.elbowL.position.y = ELBOW_Y;
    this.shoulderL.add(this.elbowL);

    this.shoulderR = new THREE.Bone();
    this.shoulderR.position.set(-SHOULDER_X, SHOULDER_Y, 0);
    this.spine.add(this.shoulderR);
    this.elbowR = new THREE.Bone();
    this.elbowR.position.y = ELBOW_Y;
    this.shoulderR.add(this.elbowR);

    this.hipL = new THREE.Bone();
    this.hipL.position.set(HIPJOINT_X, HIPJOINT_Y, 0);
    this.hips.add(this.hipL);
    this.kneeL = new THREE.Bone();
    this.kneeL.position.y = KNEE_Y;
    this.hipL.add(this.kneeL);
    this.ankleL = new THREE.Bone();
    this.ankleL.position.y = ANKLE_Y;
    this.kneeL.add(this.ankleL);

    this.hipR = new THREE.Bone();
    this.hipR.position.set(-HIPJOINT_X, HIPJOINT_Y, 0);
    this.hips.add(this.hipR);
    this.kneeR = new THREE.Bone();
    this.kneeR.position.y = KNEE_Y;
    this.hipR.add(this.kneeR);
    this.ankleR = new THREE.Bone();
    this.ankleR.position.y = ANKLE_Y;
    this.kneeR.add(this.ankleR);

    // a arma pende da espinha, nao da mao: assim ela aponta EXATAMENTE para o
    // alvo (o jogador precisa ler para onde o bot mira) e os bracos sao postos
    // por cima para encontrar a arma. O contrario exigiria IK de duas ossadas.
    // Nao e osso: o fuzil e rigido e continua sendo malha filha deste pivo.
    this.weaponPivot = new THREE.Group();
    this.spine.add(this.weaponPivot);
    this.muzzle = new THREE.Object3D();
    this.muzzle.position.set(0, 0.006, 0.545);
    this.weaponPivot.add(this.muzzle);

    /**
     * INVERSAS DE BIND = IDENTIDADE, e isso e a chave de tudo.
     *
     * O shader calcula, para cada vertice: osso.matrixWorld * inversaDeBind *
     * vertice. Com a inversa em identidade, sobra osso.matrixWorld * vertice -
     * que e LETRA POR LETRA a conta que o three fazia quando cada peca era uma
     * malha filha daquela junta. Por isso a geometria pode ficar no espaco da
     * peca (sem ser levada para o espaco do corpo) e a pose de bind nao precisa
     * existir: nao ha pose alguma para desfazer.
     *
     * A bindMatrix tambem e identidade e o modo e o padrao (attached), no qual o
     * three cancela a matriz da propria malha - ou seja, a posicao do bot vem da
     * raiz, pelos ossos, e nao da malha.
     */
    const bones = new Array(BONE_COUNT);
    bones[BONE.hips] = this.hips;
    bones[BONE.spine] = this.spine;
    bones[BONE.neck] = this.neck;
    bones[BONE.shoulderL] = this.shoulderL;
    bones[BONE.elbowL] = this.elbowL;
    bones[BONE.shoulderR] = this.shoulderR;
    bones[BONE.elbowR] = this.elbowR;
    bones[BONE.hipL] = this.hipL;
    bones[BONE.kneeL] = this.kneeL;
    bones[BONE.ankleL] = this.ankleL;
    bones[BONE.hipR] = this.hipR;
    bones[BONE.kneeR] = this.kneeR;
    bones[BONE.ankleR] = this.ankleR;

    const inverses = new Array(BONE_COUNT);
    for (let i = 0; i < BONE_COUNT; i++) inverses[i] = new THREE.Matrix4();
    this.skeleton = new THREE.Skeleton(bones, inverses);

    // --- malhas ----------------------------------------------------------
    /**
     * DUAS malhas para o corpo (uma por familia de textura) e uma para o fuzil.
     * Cada uma custa 1 chamada na cor, 1 no shadow map e 1 no NormalPass: 9 por
     * bot, contra os ~42 de antes.
     *
     * frustumCulled fica LIGADO, e por isso a esfera de culling e escrita a mao
     * (BODY_RADIUS): a esfera calculada da geometria mesclada apontaria para o
     * lugar errado, e o bot desapareceria de canto de tela.
     */
    const mkSkin = (g, mat, name) => {
      const m = new THREE.SkinnedMesh(g, mat);
      m.name = name;
      m.castShadow = true;
      m.receiveShadow = true;
      // a malha nao tem transformacao propria (a pose vem toda dos ossos)
      m.matrixAutoUpdate = false;
      m.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, BODY_CENTER_Y, 0), BODY_RADIUS);
      m.raycast = noBotRaycast;
      // layer extra: raycast de personagem acha; a camera padrao (layer 0) segue vendo
      m.layers.enable(2);
      this.root.add(m);
      m.bind(this.skeleton, BIND_IDENTITY);
      return m;
    };

    this.meshCloth = mkSkin(geo.cloth, this.clothMat, 'botCloth');
    this.meshHard = mkSkin(geo.hard, this.gearMat, 'botHard');

    this.mWeapon = new THREE.Mesh(geo.weapon, this.weaponMat);
    this.mWeapon.name = 'weapon';
    this.mWeapon.castShadow = true;
    this.mWeapon.receiveShadow = true;
    this.mWeapon.userData.surface = 'metal_thin';
    this.mWeapon.layers.enable(2);
    this.weaponPivot.add(this.mWeapon);

    /**
     * Caixas de acerto: caixas INVISIVEIS penduradas nos mesmos ossos que
     * carregam a peca correspondente. Duas garantias:
     *   - acompanham a animacao pelo mesmo caminho de sempre (matrixWorld do
     *     osso), entao mirar na cabeca continua acertando a cabeca;
     *   - tem o bounding box IDENTICO ao da peca que substituem, e bounding box
     *     e a unica coisa que o TargetRegistry le (secao 4.11).
     * Invisivel nao entra em render nenhum - nem cor, nem shadow map, nem SSAO -
     * entao custam zero draw call.
     */
    this.hitboxes = [];
    const mkHit = (g, parent, name, part) => {
      const m = new THREE.Mesh(g, getHitboxMaterial());
      m.name = name;
      m.visible = false;
      m.castShadow = false;
      m.receiveShadow = false;
      m.userData.surface = 'flesh';
      m.raycast = noBotRaycast;
      m.layers.enable(2);
      parent.add(m);
      this.hitboxes.push({ part, object: m });
      return m;
    };

    const hb = geo.hit;
    this.hbHead = mkHit(hb.head, this.neck, 'head', 'head');
    this.hbTorso = mkHit(hb.torso, this.spine, 'chest', 'chest');
    this.hbPelvis = mkHit(hb.pelvis, this.hips, 'stomach', 'stomach');
    this.hbArmL = mkHit(hb.upperArm, this.shoulderL, 'armL', 'arm');
    this.hbForeL = mkHit(hb.foreArm, this.elbowL, 'foreL', 'arm');
    this.hbArmR = mkHit(hb.upperArm, this.shoulderR, 'armR', 'arm');
    this.hbForeR = mkHit(hb.foreArm, this.elbowR, 'foreR', 'arm');
    this.hbThighL = mkHit(hb.thigh, this.hipL, 'thighL', 'leg');
    this.hbShinL = mkHit(hb.shin, this.kneeL, 'shinL', 'leg');
    this.hbThighR = mkHit(hb.thigh, this.hipR, 'thighR', 'leg');
    this.hbShinR = mkHit(hb.shin, this.kneeR, 'shinR', 'leg');
    this.hbFootL = mkHit(hb.foot, this.ankleL, 'footL', 'leg');
    this.hbFootR = mkHit(hb.foot, this.ankleR, 'footR', 'leg');

    // --- estado de animacao ---------------------------------------------
    this.phase = rnd() * TAU;
    this.idleSeed = rnd() * TAU;
    this.wAim = 0;
    this.wCrouch = 0;
    this.wMove = 0;
    this.torsoYaw = 0;
    this.headYaw = 0;
    this.pitch = 0;
    this.flinch = 0;
    this.flinchSide = 0;
    this.recoil = 0;
    this.sink = 0;
    this.dead = false;
    this.deathDone = false;
    this._t = 0;

    this._death = {
      angle: 0, vel: 0, max: 1.55, yaw: 0,
      axisX: 1, axisZ: 0, twist: 0, settle: 0
    };

    this.resetPose();
  }

  get object3D() { return this.root; }

  setVisible(v) { this.root.visible = !!v; }

  /** Posicao do mundo da boca do cano (para clarao e origem do tiro). */
  getMuzzleWorld(out) {
    this.muzzle.updateWorldMatrix(true, false);
    return out.setFromMatrixPosition(this.muzzle.matrixWorld);
  }

  /** Direcao para onde a arma aponta, no mundo. */
  getMuzzleDirection(out) {
    this.weaponPivot.updateWorldMatrix(true, false);
    out.set(0, 0, 1).transformDirection(this.weaponPivot.matrixWorld);
    return out;
  }

  resetPose() {
    this.dead = false;
    this.deathDone = false;
    this.sink = 0;
    this.flinch = 0;
    this.recoil = 0;
    this.wAim = 0;
    this.wCrouch = 0;
    this.wMove = 0;
    this._death.angle = 0;
    this._death.vel = 0;
    this._death.settle = 0;
    this.root.quaternion.identity();
    this.root.rotation.set(0, 0, 0);
    this.root.scale.set(1, 1, 1);
    this.hips.position.set(0, HIP_Y, 0);
    this.hips.rotation.set(0, 0, 0);
    this.spine.rotation.set(0, 0, 0);
    this.neck.rotation.set(0, 0, 0);
    this.shoulderL.rotation.set(0, 0, 0);
    this.shoulderR.rotation.set(0, 0, 0);
    this.elbowL.rotation.set(0, 0, 0);
    this.elbowR.rotation.set(0, 0, 0);
    this.hipL.rotation.set(0, 0, 0);
    this.hipR.rotation.set(0, 0, 0);
    this.kneeL.rotation.set(0, 0, 0);
    this.kneeR.rotation.set(0, 0, 0);
    this.ankleL.rotation.set(0, 0, 0);
    this.ankleR.rotation.set(0, 0, 0);
    this.hipL.quaternion.identity();
    this.hipR.quaternion.identity();
    this.shoulderL.quaternion.identity();
    this.shoulderR.quaternion.identity();
    this.root.visible = true;
  }

  /** Tranco ao levar tiro: um empurrao curto no torso, decai sozinho. */
  addFlinch(amount, sideSign) {
    this.flinch = Math.min(0.55, this.flinch + amount);
    this.flinchSide = sideSign >= 0 ? 1 : -1;
  }

  /** Coice: chamado a cada disparo, some em ~0.1 s. */
  addRecoil(amount) {
    this.recoil = Math.min(0.35, this.recoil + amount);
  }

  /**
   * Comeca a morte. dirX/dirZ e a direcao do tiro (do atirador para o bot):
   * o corpo tomba PARA esse lado, e nao para um lado sorteado.
   */
  playDeath(dirX, dirZ, yaw, headshot) {
    if (this.dead) return;
    this.dead = true;
    this.deathDone = false;
    const len = Math.hypot(dirX, dirZ) || 1;
    const dx = dirX / len;
    const dz = dirZ / len;
    // eixo tal que (eixo x cima) aponte para a direcao do impacto
    const d = this._death;
    d.axisX = dz;
    d.axisZ = -dx;
    d.yaw = yaw;
    d.angle = 0;
    d.vel = headshot ? 1.7 : 0.8 + this.rnd() * 0.7;
    d.max = 1.50 + this.rnd() * 0.12;
    d.twist = (this.rnd() - 0.5) * 0.7;
    d.settle = 0;
  }

  /** Afundamento do corpo antes de sumir (evita corpo piscando fora da tela). */
  setSink(amount) {
    this.sink = amount;
  }

  // -------------------------------------------------------------------------
  // animacao
  // -------------------------------------------------------------------------

  /**
   * @param {number} dt
   * @param {object} p pose desejada (objeto reaproveitado pelo Bot, nunca novo):
   *   speed, crouch, aim, torsoYaw, pitch, scanYaw, reload, moving
   */
  update(dt, p) {
    this._t += dt;
    if (this.dead) { this._updateDeath(dt); return; }

    const speed = p.speed || 0;
    const moving = speed > 0.2;

    // pesos suavizados: transicao dura entre poses e o que denuncia animacao procedural
    const kA = Math.min(1, dt * 9);
    const kC = Math.min(1, dt * 7);
    this.wAim += ((p.aim || 0) - this.wAim) * kA;
    this.wCrouch += ((p.crouch || 0) - this.wCrouch) * kC;
    this.wMove += ((moving ? 1 : 0) - this.wMove) * Math.min(1, dt * 6);

    const aim = this.wAim;
    const crouch = this.wCrouch;

    // --- ciclo de passo ---------------------------------------------------
    // frequencia proporcional a velocidade: andar ~1.4 Hz, correr ~2.7 Hz
    const freq = moving ? (0.80 + speed * 0.30) : 0;
    this.phase += dt * freq * TAU;
    if (this.phase > TAU) this.phase -= TAU;
    if (this.phase < 0) this.phase += TAU;

    const s = Math.sin(this.phase);
    const c = Math.cos(this.phase);
    const gait = this.wMove * (1 - crouch * 0.45);
    /** 1 parado, 0 andando: tudo que e postura de parado entra por aqui. */
    const stand = 1 - this.wMove;

    // Peso passando de um pe para o outro. Sem isso o bot parado e uma estatua -
    // e estatua o olho identifica em meio segundo, mesmo de longe.
    const sway = Math.sin(this._t * IDLE_SWAY_HZ + this.idleSeed) * IDLE_SWAY * stand;

    // --- quadril: sobe/desce, balanca e rola --------------------------------
    // Vem ANTES das pernas porque a altura do quadril e o que define onde os pes
    // precisam alcancar. Ordem invertida = pe furando o chao ao agachar.
    const bob = -Math.abs(s) * 0.032 * gait * Math.min(1, speed / 3.5);
    // Desce o quadril na proporcao da passada: de perna esticada o pe nao
    // ALCANCA a frente, e a IK trava - e isso vira deslizamento no chao.
    const strideDrop = Math.min(0.62, speed / (4 * Math.max(0.5, freq))) * this.wMove * 0.16;
    this.hips.position.y = HIP_Y - crouch * 0.36 - strideDrop - STANCE_DROP * stand + bob - this.sink;
    this.hips.position.x = s * 0.022 * gait + sway;
    this.hips.rotation.z = s * 0.040 * gait + sway * 0.8;
    this.hips.rotation.y = -s * 0.075 * gait * (1 - aim * 0.5);

    // --- pernas por IK: o pe e colocado no CHAO, a perna se dobra sozinha ----
    // Passo procedural: o pe avanca e recua em seno; a elevacao so acontece na
    // meia-volta em que ele vai para a frente (fase de balanco), o que evita o
    // classico "pe deslizando" de animacao feita so com rotacao de junta.
    // Amplitude EXATA que zera o deslizamento do pe: com 2 passos por ciclo, o
    // pe precisa percorrer, em relacao ao corpo, a mesma distancia que o corpo
    // percorre no chao. Chutar a amplitude e o que produz o classico "patinando".
    const stride = Math.min(0.62, speed / (4 * Math.max(0.5, freq))) * this.wMove;
    const lift = (0.06 + Math.min(0.12, speed * 0.026)) * gait;
    // alvo do TORNOZELO (nao do chao): a sola fica 7.5 cm abaixo dele
    const groundY = -this.hips.position.y + ANKLE_HEIGHT;
    const kneeOut = 0.22 + crouch * 0.30;

    // agachado os pes recuam um pouco: o centro de massa vai para tras
    const footBack = -crouch * 0.10;

    const cL = Math.cos(this.phase);
    const cR = Math.cos(this.phase + Math.PI);
    const sR = -s;

    // Postura de parado. Some sozinha ao andar (stand -> 0), entao o ciclo de
    // passo continua exatamente como era. O `- sway` desconta a translacao do
    // quadril: o alvo do pe e escrito no espaco do QUADRIL, entao sem esse
    // desconto o peso passando de um lado para o outro arrastaria os dois pes
    // pelo chao junto com o corpo.
    const stanceX = STANCE_WIDTH * stand;
    const toe = STANCE_TOE * stand;

    this._solveLeg(this.hipL, this.kneeL, this.ankleL,
      HIPJOINT_X + stanceX - sway,
      groundY + Math.max(0, cL) * lift,
      footBack + s * stride + STANCE_LEAD * stand,
      kneeOut, toe);
    this._solveLeg(this.hipR, this.kneeR, this.ankleR,
      -(HIPJOINT_X + stanceX) - sway,
      groundY + Math.max(0, cR) * lift,
      footBack + sR * stride + STANCE_TRAIL * stand,
      -kneeOut, -toe);

    // --- torso: gira independente das pernas --------------------------------
    const twist = clamp(p.torsoYaw || 0, -0.95, 0.95);
    this.torsoYaw += (twist - this.torsoYaw) * Math.min(1, dt * 10);
    const pitch = clamp(p.pitch || 0, -0.9, 0.75);
    this.pitch += (pitch - this.pitch) * Math.min(1, dt * 10);

    const breathe = Math.sin(this._t * 1.5 + this.idleSeed) * 0.014 * (1 - this.wMove * 0.6);
    this.spine.rotation.y = this.torsoYaw + s * 0.055 * gait * (1 - aim * 0.7);
    this.spine.rotation.x = 0.045 + crouch * 0.22 + aim * 0.10 + Math.min(0.16, speed * 0.022) * gait
      + breathe + this.flinch * 0.55 - this.recoil * 0.10;
    this.spine.rotation.z = this.flinch * 0.35 * this.flinchSide - s * 0.018 * gait;

    // --- cabeca -------------------------------------------------------------
    const scan = p.scanYaw || 0;
    const wantHeadYaw = clamp((p.torsoYaw || 0) - this.torsoYaw + scan, -0.85, 0.85);
    this.headYaw += (wantHeadYaw - this.headYaw) * Math.min(1, dt * 7);
    this.neck.rotation.y = this.headYaw;
    this.neck.rotation.x = -this.pitch * 0.55 - this.spine.rotation.x * 0.6 + 0.03;
    this.neck.rotation.z = -this.headYaw * 0.12;

    // --- arma ---------------------------------------------------------------
    // Carregada (baixa, atravessada no corpo) -> ombreada (na linha do olho).
    // A arma e posicionada PRIMEIRO; os bracos vao atras por IK. O balanco de
    // andar entra na arma, nao nos bracos: soldado com fuzil em punho nao
    // balanca os bracos, e fingir que balanca fica errado.
    const recoilKick = this.recoil;
    const wob = s * 0.020 * gait * (1 - aim * 0.55);
    const rl = p.reload || 0;
    const ph = rl > 0 ? Math.sin(Math.min(1, rl) * Math.PI) : 0;

    this.weaponPivot.position.set(
      -0.150 + aim * 0.104 + wob * 0.5,
      0.180 + aim * 0.126 - recoilKick * 0.015 - ph * 0.050 + wob,
      0.150 - aim * 0.010 - recoilKick * 0.055
    );
    // O -spine.rotation.x compensa a inclinacao do tronco: sem isso o cano sai
    // 8 graus abaixo do angulo pedido e o bot atira no chao a 30 m.
    this.weaponPivot.rotation.set(
      0.40 * (1 - aim) - (this.pitch + this.spine.rotation.x) * aim - recoilKick * 0.55 + wob * 0.8,
      0.34 * (1 - aim) + ph * 0.26,
      -0.14 * (1 - aim) + recoilKick * 0.10 + ph * 0.50
    );
    this.weaponPivot.updateMatrix(); // a IK le a matriz da arma logo abaixo

    // --- bracos por IK: as duas maos ficam presas na arma --------------------
    // punho da mao de disparo, no espaco local da arma
    _ikT.set(0, -0.032, -0.078).applyMatrix4(this.weaponPivot.matrix);
    solveLimb(this.shoulderR, this.elbowR, _ikT.x, _ikT.y, _ikT.z,
      -0.60, -1.0, -0.30 - aim * 0.25, ARM_UPPER, ARM_FORE);

    // mao de apoio no guarda-mao; na recarga ela desce ate o carregador
    _ikT.set(0, -0.020 - ph * 0.085, 0.235 - ph * 0.240).applyMatrix4(this.weaponPivot.matrix);
    solveLimb(this.shoulderL, this.elbowL, _ikT.x, _ikT.y, _ikT.z,
      0.55 + aim * 0.10, -1.0, -0.10, ARM_UPPER, ARM_FORE);

    if (ph > 0) this.neck.rotation.x += ph * 0.14;


    // decaimentos
    this.flinch *= Math.max(0, 1 - dt * 6.5);
    this.recoil *= Math.max(0, 1 - dt * 13);
  }

  /**
   * Resolve uma perna e deixa a SOLA paralela ao chao.
   *
   * O alvo (x, y, z) e o tornozelo no espaco do quadril. Depois da IK, o pe
   * herdaria a inclinacao da canela - com a perna dobrada isso poria o bot na
   * ponta do pe. Entao calculamos o angulo exato que devolve a sola ao
   * horizontal: projetando o "para frente" e o "para cima" do pe no espaco do
   * quadril, o angulo que zera a componente vertical do "para frente" e
   * atan2(frente.y, cima.y).
   *
   * `toeYaw` abre a ponta do pe. O eixo Y do tornozelo e o eixo da PERNA (a base
   * que solveLimb monta tem Y = -osso), entao girar nele roda a bota em torno da
   * canela, que e exatamente o que se quer. Sinal medido: yaw positivo leva a
   * ponta para +X do mundo, e +X e o lado ESQUERDO de um corpo que aponta para
   * +Z - logo o pe esquerdo abre com yaw positivo e o direito com negativo.
   * O nivelamento da sola nao se perde: o eixo X do tornozelo fica horizontal
   * com a perna quase em pe, entao o yaw nao devolve componente vertical.
   */
  _solveLeg(hip, knee, ankle, x, y, z, poleX, toeYaw) {
    solveLimb(hip, knee, x, y, z, poleX, 0.05, 1.0, LEG_UPPER, LEG_LOWER);
    _qa.setFromEuler(knee.rotation);
    _qb.copy(hip.quaternion).multiply(_qa);
    _ikY.set(0, 0, 1).applyQuaternion(_qb);   // frente do pe
    _ikZ.set(0, 1, 0).applyQuaternion(_qb);   // cima do pe
    ankle.rotation.set(Math.atan2(_ikY.y, _ikZ.y), toeYaw || 0, 0);
  }

  /**
   * Morte: ragdoll simplificado de 5 segmentos (raiz, espinha, cabeca, bracos,
   * pernas). A raiz tomba em torno dos pes com aceleracao e uma batida
   * amortecida no chao; os membros perseguem uma pose mole com molas de
   * constantes diferentes, o que gera o atraso que faz parecer peso.
   */
  _updateDeath(dt) {
    const d = this._death;
    const step = Math.min(dt, 1 / 30);

    if (!this.deathDone) {
      d.vel += 9.2 * step * Math.max(0.22, Math.cos(d.angle * 0.75));
      d.angle += d.vel * step;
      if (d.angle >= d.max) {
        // batida no chao com quique amortecido: cair e parar seco parece bloco
        d.angle = d.max - (d.angle - d.max) * 0.30;
        d.vel = -d.vel * 0.22;
        d.settle += 1;
        if (Math.abs(d.vel) < 0.75 || d.settle > 2) { d.vel = 0; d.angle = d.max; this.deathDone = true; }
      }
    }

    _axis.set(d.axisX, 0, d.axisZ);
    if (_axis.lengthSq() < 1e-6) _axis.set(1, 0, 0);
    _axis.normalize();
    _q1.setFromAxisAngle(_axis, d.angle);
    _q2.setFromAxisAngle(_upAxis, d.yaw);
    this.root.quaternion.copy(_q1).multiply(_q2);

    // O corpo gira em torno dos PES. Deitado, joelho e mochila projetados para
    // frente furariam o asfalto - entao o quadril sobe na vertical do MUNDO
    // (por isso o deslocamento e convertido para o espaco ja girado da raiz).
    const t = Math.min(1, d.angle / d.max);
    const lift = DEATH_LIFT * Math.sin(d.angle) - this.sink;
    _qa.copy(this.root.quaternion).invert();
    _ikT.set(0, lift, 0).applyQuaternion(_qa);
    this.hips.position.set(_ikT.x, HIP_Y - 0.06 * t + _ikT.y, _ikT.z);
    this.hips.rotation.set(0, d.twist * t, d.twist * t * 0.6);

    // Interpolacao em QUATERNION, nao em Euler: os membros vem da IK, cuja
    // decomposicao em Euler pode estar girada 180 graus em Y. Interpolar esse
    // Euler faria a perna dar um giro completo no meio da queda; o slerp pega
    // sempre o caminho curto.
    const k1 = Math.min(1, step * 3.4);
    const k2 = Math.min(1, step * 5.0);
    const k3 = Math.min(1, step * 2.6);

    _eu.set(0.28, d.twist * 0.40, -d.twist * 0.50);
    this.spine.quaternion.slerp(_qa.setFromEuler(_eu), k1);
    _eu.set(0.55, d.twist * 0.90, d.twist * 0.40);
    this.neck.quaternion.slerp(_qa.setFromEuler(_eu), k2);

    this.shoulderL.quaternion.slerp(DEATH_POSE.shoulderL, k3);
    this.elbowL.quaternion.slerp(DEATH_POSE.elbowL, k3);
    this.shoulderR.quaternion.slerp(DEATH_POSE.shoulderR, k3);
    this.elbowR.quaternion.slerp(DEATH_POSE.elbowR, k3);
    this.hipL.quaternion.slerp(DEATH_POSE.hipL, k1);
    this.kneeL.quaternion.slerp(DEATH_POSE.kneeL, k1);
    this.ankleL.quaternion.slerp(DEATH_POSE.ankleL, k1);
    this.hipR.quaternion.slerp(DEATH_POSE.hipR, k1);
    this.kneeR.quaternion.slerp(DEATH_POSE.kneeR, k1);
    this.ankleR.quaternion.slerp(DEATH_POSE.ankleR, k1);

    // a arma escorrega da mao e fica caida junto ao corpo
    approach(this.weaponPivot.position, 'x', -0.24, k3);
    approach(this.weaponPivot.position, 'y', 0.04, k3);
    approach(this.weaponPivot.position, 'z', 0.08, k3);
    approach(this.weaponPivot.rotation, 'x', 0.30, k3);
    approach(this.weaponPivot.rotation, 'y', 0.20, k3);
    approach(this.weaponPivot.rotation, 'z', -0.85, k3);
  }

  dispose() {
    // as texturas e as geometrias sao COMPARTILHADAS (nao clonadas): quem as
    // libera e disposeSharedBotGeometry, no dispose do BotManager
    this.clothMat.dispose();
    this.gearMat.dispose();
    this.weaponMat.dispose();
    // o esqueleto tem uma textura de ossos criada pelo renderer: e por bot
    this.skeleton?.dispose?.();
    if (this.root.parent) this.root.parent.remove(this.root);
  }
}

function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
function approach(obj, key, target, k) { obj[key] += (target - obj[key]) * k; }
